import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './QuicklinksForm.css';

export default function QuickLinksForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [sectionId, setSectionId] = useState(42);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});

  // Form values (auto-filled from content_published)
  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    cards: [
      { id: 'apply', title: '', text: '' },
      { id: 'calendar', title: '', text: '' },
      { id: 'contact', title: '', text: '' }
    ]
  });

  // Pending approvals per field
  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    'cards.0': false,
    'cards.1': false,
    'cards.2': false
  });

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({...formData});

  useEffect(() => {
    loadQuickLinksSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadQuickLinksSection = async () => {
    setLoading(true);
    try {
      // Use same endpoint as HeroSectionForm - section ID 42 for quick links
      const response = await fetch(
        `${apiBase}/cms/section/42/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const { section, pending_changes } = result.data;
        
        if (section && section.content_published) {
          let published = section.content_published;
          
          if (typeof published === 'string') {
            try {
              published = JSON.parse(published);
            } catch (e) {
              published = {};
            }
          }

          const publishedEn = published.en || {};

          // Auto-fill form with published data
          const initialData = {
            kicker: publishedEn.kicker || '',
            title: publishedEn.title || '',
            cards: [
              { id: 'apply', title: publishedEn.cards?.[0]?.title || '', text: publishedEn.cards?.[0]?.text || '' },
              { id: 'calendar', title: publishedEn.cards?.[1]?.title || '', text: publishedEn.cards?.[1]?.text || '' },
              { id: 'contact', title: publishedEn.cards?.[2]?.title || '', text: publishedEn.cards?.[2]?.text || '' }
            ]
          };

          setFormData(initialData);
          setOriginalData(initialData);
          setSectionId(section.id);

          // Mark pending fields
          if (Array.isArray(pending_changes)) {
            const pendingMap = {
              kicker: false,
              title: false,
              'cards.0': false,
              'cards.1': false,
              'cards.2': false
            };

            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                pendingMap[change.field_id] = true;
              }
            });

            setPendingFields(pendingMap);
          }
        }
      }
    } catch (err) {
      toast.error('Error loading quick links section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleCardChange = (cardIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      cards: prev.cards.map((card, idx) =>
        idx === cardIndex ? { ...card, [field]: value } : card
      )
    }));
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Check if value changed
    const hasChanged = fieldName.startsWith('cards.')
      ? JSON.stringify(formData[fieldName.split('.')[0]][parseInt(fieldName.split('.')[1])]) !== 
        JSON.stringify(originalData[fieldName.split('.')[0]][parseInt(fieldName.split('.')[1])])
      : formData[fieldName] !== originalData[fieldName];

    if (!hasChanged) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const newValue = fieldName.startsWith('cards.')
        ? formData.cards[parseInt(fieldName.split('.')[1])]
        : formData[fieldName];

      const oldValue = fieldName.startsWith('cards.')
        ? originalData.cards[parseInt(fieldName.split('.')[1])]
        : originalData[fieldName];

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' ').replace(/\./g, ' '),
            old_value: oldValue,
            new_value: newValue,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Submitted for approval`);
        
        // Mark as pending locally
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        // Update original data
        if (fieldName.startsWith('cards.')) {
          const cardIdx = parseInt(fieldName.split('.')[1]);
          setOriginalData(prev => ({
            ...prev,
            cards: prev.cards.map((card, idx) =>
              idx === cardIdx ? { ...formData.cards[cardIdx] } : card
            )
          }));
        } else {
          setOriginalData(prev => ({
            ...prev,
            [fieldName]: formData[fieldName]
          }));
        }
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const getCardLabel = (id) => {
    const labels = {
      'apply': 'Apply Now',
      'calendar': 'School Calendar',
      'contact': 'Contact Us'
    };
    return labels[id] || id;
  };

  return (
    <div className="quicklinks-wrapper">
      <ToastContainer />

      <div className="quicklinks-header">
        <h2 className="quicklinks-title">Quick Links Section</h2>
        <p className="quicklinks-description">Edit and submit for approval</p>
      </div>

      {/* Header Fields Section */}
      <div className="quicklinks-header-section">
        <div className="header-field">
          <div className="header-field-label">
            <label>Kicker / Subtitle</label>
            {pendingFields.kicker && <span className="pending-badge">Pending</span>}
          </div>
          <input 
            type="text" 
            maxLength="100" 
            value={formData.kicker} 
            onChange={(e) => handleFieldChange('kicker', e.target.value)} 
            disabled={submitting.kicker || pendingFields.kicker} 
            placeholder="e.g., Quick Access" 
            className="header-input" 
          />
          <button 
            onClick={() => handleSubmitField('kicker')} 
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker} 
            className="submit-button-sm"
          >
            {submitting.kicker ? 'Submitting...' : 'Submit'}
          </button>
        </div>

        <div className="header-field">
          <div className="header-field-label">
            <label>Section Title</label>
            {pendingFields.title && <span className="pending-badge">Pending</span>}
          </div>
          <input 
            type="text" 
            maxLength="150" 
            value={formData.title} 
            onChange={(e) => handleFieldChange('title', e.target.value)} 
            disabled={submitting.title || pendingFields.title} 
            placeholder="e.g., Everything you need at a glance" 
            className="header-input" 
          />
          <button 
            onClick={() => handleSubmitField('title')} 
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title} 
            className="submit-button-sm"
          >
            {submitting.title ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>

      {/* Cards Section */}
      <div className="quicklinks-cards-section">
        <h3 className="cards-section-title">Quick Links Cards</h3>
        <p className="cards-section-desc">Edit individual cards below</p>

        <div className="cards-grid">
          {/* Card 1: Apply Now */}
          <div className="card-wrapper">
            <div className="card-top">
              <h4 className="card-title">Apply Now</h4>
              {pendingFields['cards.0'] && <span className="pending-badge-small">Pending</span>}
            </div>
            <div className="card-content">
              <div className="card-field">
                <label>Title</label>
                <input 
                  type="text" 
                  maxLength="50" 
                  value={formData.cards[0].title} 
                  onChange={(e) => handleCardChange(0, 'title', e.target.value)} 
                  disabled={submitting['cards.0'] || pendingFields['cards.0']} 
                  placeholder="e.g., Apply Now" 
                  className="card-input" 
                />
              </div>
              <div className="card-field">
                <label>Description</label>
                <textarea 
                  maxLength="200" 
                  value={formData.cards[0].text} 
                  onChange={(e) => handleCardChange(0, 'text', e.target.value)} 
                  disabled={submitting['cards.0'] || pendingFields['cards.0']} 
                  placeholder="Describe this card..." 
                  className="card-textarea" 
                  rows="3"
                />
              </div>
            </div>
            <button 
              onClick={() => handleSubmitField('cards.0')} 
              disabled={submitting['cards.0'] || pendingFields['cards.0'] || (formData.cards[0].title === originalData.cards[0].title && formData.cards[0].text === originalData.cards[0].text)} 
              className="submit-button-card"
            >
              {submitting['cards.0'] ? 'Submitting...' : 'Submit'}
            </button>
          </div>

          {/* Card 2: School Calendar */}
          <div className="card-wrapper">
            <div className="card-top">
              <h4 className="card-title">School Calendar</h4>
              {pendingFields['cards.1'] && <span className="pending-badge-small">Pending</span>}
            </div>
            <div className="card-content">
              <div className="card-field">
                <label>Title</label>
                <input 
                  type="text" 
                  maxLength="50" 
                  value={formData.cards[1].title} 
                  onChange={(e) => handleCardChange(1, 'title', e.target.value)} 
                  disabled={submitting['cards.1'] || pendingFields['cards.1']} 
                  placeholder="e.g., School Calendar" 
                  className="card-input" 
                />
              </div>
              <div className="card-field">
                <label>Description</label>
                <textarea 
                  maxLength="200" 
                  value={formData.cards[1].text} 
                  onChange={(e) => handleCardChange(1, 'text', e.target.value)} 
                  disabled={submitting['cards.1'] || pendingFields['cards.1']} 
                  placeholder="Describe this card..." 
                  className="card-textarea" 
                  rows="3"
                />
              </div>
            </div>
            <button 
              onClick={() => handleSubmitField('cards.1')} 
              disabled={submitting['cards.1'] || pendingFields['cards.1'] || (formData.cards[1].title === originalData.cards[1].title && formData.cards[1].text === originalData.cards[1].text)} 
              className="submit-button-card"
            >
              {submitting['cards.1'] ? 'Submitting...' : 'Submit'}
            </button>
          </div>

          {/* Card 3: Contact Us */}
          <div className="card-wrapper">
            <div className="card-top">
              <h4 className="card-title">Contact Us</h4>
              {pendingFields['cards.2'] && <span className="pending-badge-small">Pending</span>}
            </div>
            <div className="card-content">
              <div className="card-field">
                <label>Title</label>
                <input 
                  type="text" 
                  maxLength="50" 
                  value={formData.cards[2].title} 
                  onChange={(e) => handleCardChange(2, 'title', e.target.value)} 
                  disabled={submitting['cards.2'] || pendingFields['cards.2']} 
                  placeholder="e.g., Contact Us" 
                  className="card-input" 
                />
              </div>
              <div className="card-field">
                <label>Description</label>
                <textarea 
                  maxLength="200" 
                  value={formData.cards[2].text} 
                  onChange={(e) => handleCardChange(2, 'text', e.target.value)} 
                  disabled={submitting['cards.2'] || pendingFields['cards.2']} 
                  placeholder="Describe this card..." 
                  className="card-textarea" 
                  rows="3"
                />
              </div>
            </div>
            <button 
              onClick={() => handleSubmitField('cards.2')} 
              disabled={submitting['cards.2'] || pendingFields['cards.2'] || (formData.cards[2].title === originalData.cards[2].title && formData.cards[2].text === originalData.cards[2].text)} 
              className="submit-button-card"
            >
              {submitting['cards.2'] ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
